import * as React from "react";
import {useEffect, useImperativeHandle, useState} from "react";
import {Trash2, RotateCcw} from "lucide-react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { toast } from "sonner";
import {ProviderFile} from "../../models/ProviderFile";
import {MigrationStatus} from "../../models/MigrationStatus";
import ProvidersRepository from "../../repositories/ProvidersRepository";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobsRepository from "../../repositories/JobsRepository";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import TextHighlighter from "../common/TextHighlighter";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {useTableFileFilters} from "../common/Table/Hooks/UseTableFileFilters";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {FileExtensionsFilter} from "../common/Table/File/FileExtensionsFilter";
import {NumberInput} from "../common/Table/NumberInput";
import {FileStatusFilter} from "../common/Table/File/FileStatusFilter";
import {SearchInput} from "../common/Table/SearchInput";
import TablePagination from "../common/Table/TablePagination";
import {ActiveJobsExplorer} from "../../models/Jobs/ActiveJobsExplorer";
import {FileFailureTypeFilter} from "../common/Table/File/FileFailureTypeFilter";
import {FileFailureTypeChip} from "../common/Table/File/FileFailureTypeChip";
import {FileConflictFilter} from "../common/Table/File/FileConflictFilter";
import {FileConflictChip} from "../common/Table/File/FileConflictChip";
import {cn} from "@/lib/utils";
import {
  fileFiltersBoxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import TableEmptyState from "../common/TableEmptyState";

interface ProviderFilesTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveJobsExplorer;
  newProviderFilesForDeletion: ProviderFile[];
  setNewProvidersFilesForDeletion: React.Dispatch<React.SetStateAction<ProviderFile[]>>;
  providerId?: string;
  disabled?: boolean;
  toolbar?: React.ReactNode;
}

export interface ProviderFilesTableRef {
  exportCurrentViewToCsv: (reason?: string) => void;
}

export function ProviderFilesTable({
  ref,
  organizationId,
  activeJobsExplorer,
  newProviderFilesForDeletion,
  setNewProvidersFilesForDeletion,
  providerId,
  disabled,
  toolbar
}: ProviderFilesTableProps & { ref?: React.Ref<ProviderFilesTableRef> }) {
  const providersRepository = new ProvidersRepository();
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [providerFiles, setProviderFiles] = useState<ProviderFile[]>([]);
  const [conflictReasonFilter, setConflictReasonFilter] = React.useState<string | null>(null);
  const [conflictStatusFilter, setConflictStatusFilter] = React.useState<string | null>(null);

  const {
    order,
    sort,
    page,
    rowsPerPage,
    rows,
    setRows,
    setPage,
    changeSort,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "asc",
    sort: "status",
    page: 0,
    rowsPerPage: 100
  });

  const {
    statusFilter,
    failureType,
    filePathFilter,
    destinationPathFilter,
    minSizeFilter,
    maxSizeFilter,
    extensions,
    extensionsSearch,
    setStatusFilter,
    setFailureType,
    setExtensions,
    setExtensionsSearch,
    handleDestinationPathSearch,
    handleFilePathSearch,
    handleMaxSizeFilter,
    handleMinSizeFilter
  } = useTableFileFilters();

  const providerFilesQuery = useQuery({ queryKey: [
      QUERY_KEYS.PROVIDER_FILES,
      organizationId,
      providerId,
      page,
      rowsPerPage,
      statusFilter,
      sort,
      order,
      extensionsSearch,
      filePathFilter,
      destinationPathFilter,
      minSizeFilter,
      maxSizeFilter,
      conflictReasonFilter,
      conflictStatusFilter,
    ], queryFn:
    async () =>  await providersRepository.getProviderFiles(providerId, {
      descending: order === "desc",
      orderBy: sort,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      failureType: failureType,
      status: statusFilter
        ? [statusFilter]
        : null,
      extension: extensionsSearch.length > 0
        ? extensionsSearch
        : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (providerFilesQuery.data?.data) {
      setProviderFiles(providerFilesQuery.data?.data);
      setRows(providerFilesQuery.data.paging?.totalCount ?? 0);
    }
  }, [providerFilesQuery]);

  const exportCurrentViewToCsv = async (reason?: string) => {
    const options = {
      descending: order === "desc",
      orderBy: sort,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      failureType: failureType,
      status: statusFilter
        ? [statusFilter]
        : null,
      extension: extensionsSearch.length > 0
        ? extensionsSearch
        : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    };
    try {
      await jobsRepository.startCsvGenerationFromProviderFiles(organizationId, options, reason, providerId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_PROVIDER_FILES, providerId] });
      toast.success("Provider files export started");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to start provider files export", { duration: 8000 });
    }
  };

  useImperativeHandle(ref, () => ({
    exportCurrentViewToCsv,
  }));

  const handleMarkNotFoundFileForRetry = async (providerId: string, fileId: string, reason: string) => {
    await providersRepository.markNotFoundFileForRetry(providerId, fileId, reason);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROVIDER_FILES] });
  }

  const handleAddProviderFileForDeletion = (providerFileEntity: ProviderFile) => {
    if (!disabledRows.has(providerFileEntity.id))
      setNewProvidersFilesForDeletion((prevRows) => [...prevRows, providerFileEntity,]);
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return new Set(newProviderFilesForDeletion.map((c) => c.id));
    }
    return new Set<string>();
  };

  const getActiveButtonForExplorer = (
    providerFileEntity: ProviderFile,
    isDisabled: boolean
  ) => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return (
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-destructive"
            aria-label="Delete"
            disabled={isDisabled || disabled}
            onClick={() =>
              handleAddProviderFileForDeletion(providerFileEntity)
            }
          >
            <Trash2 className="size-4" />
          </Button>
        );

      default:
        <></>;
    }
  };

  const disabledRows = getDisabledRows();
  const loading = providerFilesQuery.isFetching || providerFilesQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {toolbar}
      <div className={fileFiltersBoxClassName}>
        <FileExtensionsFilter
          extensions={extensions}
          onExtensionsChange={setExtensions}
          onSearch={() => setExtensionsSearch(extensions)}
        />
        <NumberInput
          label="Files min size"
          placeholder="Min size"
          onBlur={(e) => handleMinSizeFilter(e.target.value)}
        />
        <NumberInput
          label="Files max size"
          placeholder="Max size"
          onBlur={(e) => handleMaxSizeFilter(e.target.value)}
        />
        <FileStatusFilter
          value={statusFilter}
          onChange={(newValue) => {
            setPage(0);
            setStatusFilter(newValue);
          }}
        />
        <FileFailureTypeFilter
          value={failureType}
          onChange={(newValue) => {
            setPage(0);
            setFailureType(newValue);
          }}
        />
        <FileConflictFilter
          reason={conflictReasonFilter}
          status={conflictStatusFilter}
          onReasonChange={(newValue) => {
            setPage(0);
            setConflictReasonFilter(newValue);
          }}
          onStatusChange={(newValue) => {
            setPage(0);
            setConflictStatusFilter(newValue);
          }}
        />
      </div>
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Source path / API ID"
          placeholder="File path or source API ID"
          onBlur={(e) => handleFilePathSearch(e.target.value)}
        />
        <SearchInput
          label="Destination path"
          placeholder="SharePoint path"
          onBlur={(e) => handleDestinationPathSearch(e.target.value)}
        />
      </div>
      <div className={cn(
        tableWrapperClassName,
        loading && "opacity-50 pointer-events-none"
      )}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              {providerId === undefined && (
                <TableHead className="w-[12%]">
                  <SortableTableColumnHeader
                    label={"Provider ID"}
                    sortProperty={"providerId"}
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
              )}
              <TableHead className="w-[13%]">
                <SortableTableColumnHeader
                  label={"Status"}
                  sortProperty={"status"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[16%]">
                <SortableTableColumnHeader
                  label={"Failure Reason"}
                  sortProperty={"failureType"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[12%]">
                Conflict
              </TableHead>
              <TableHead className="w-[21%]">
                File Path / Source Api File ID
              </TableHead>
              <TableHead className="w-[21%]">
                Destination Path
              </TableHead>
              <TableHead className="w-[9%]">
                <SortableTableColumnHeader
                  label={"Size (MB)"}
                  sortProperty={"fileSize"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[8%]">
                Extension
              </TableHead>
              <TableHead className="w-[50px]" />
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {providerFiles.map((providerFile: ProviderFile) => {
              const isDisabled = disabledRows.has(providerFile.id);
              return (
                <TableRow key={providerFile.id}>
                  {providerId === undefined && (
                    <TableCell className="whitespace-normal break-all">
                      <a href={`/organization/${organizationId}/provider/${providerFile.providerId}`} className="underline hover:no-underline">
                        {providerFile.providerId}
                      </a>
                    </TableCell>
                  )}
                  <TableCell>
                    <MigrationStatusChip status={providerFile.status} isFile />
                  </TableCell>
                  <TableCell>
                    <FileFailureTypeChip
                      failureType={providerFile.failureType}
                      failureReason={providerFile.failureReason}
                    />
                  </TableCell>
                  <TableCell>
                    <FileConflictChip
                      conflictReason={providerFile.conflictReason}
                      conflictStatus={providerFile.conflictStatus}
                    />
                  </TableCell>
                  <TableCell className="whitespace-normal break-all">
                    <TextHighlighter
                      text={providerFile.filePath}
                      searchTerm={filePathFilter}
                      level="body-sm"
                    />
                  </TableCell>
                  <TableCell className="whitespace-normal break-all">
                    <TextHighlighter
                      text={providerFile.destinationPath}
                      searchTerm={destinationPathFilter}
                      level="body-sm"
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral">{String(providerFile.fileSizeInMb?.toFixed(2))}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="primary">{providerFile.extension ?? "-"}</Badge>
                  </TableCell>
                  <TableCell>
                    {getActiveButtonForExplorer(providerFile, isDisabled)}
                  </TableCell>
                  <TableCell>
                    {providerFile.status === MigrationStatus.NotFound && (
                      <ActionConfirmationModal
                        actionConfig={ACTION_CONFIGS.MARK_NOT_FOUND_FILE_FOR_RETRY}
                        onConfirm={async (reason) =>
                          await handleMarkNotFoundFileForRetry(providerId === undefined ? providerFile.providerId : providerId, providerFile.id, reason)}
                        buttonProps={{
                          disabled: isDisabled || disabled,
                          size: "sm",
                          color: "primary"
                        }}
                      tooltip="Mark for retry"
                        >
                          <Button variant="ghost" size="icon-sm" className="text-primary" aria-label="MarkForRetry">
                                <RotateCcw className="size-4" />
                              </Button>
                      </ActionConfirmationModal>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
              {providerFiles.length === 0 && !providerFilesQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No provider files found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};
